import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authLogin, authRegister, authGoogle, authMe, authLogout } from "./api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = logged
  const [authOpen, setAuthOpen] = useState(false);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await authMe();
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Google OAuth callback: #session_id=...
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        authGoogle(sid)
          .then(({ data }) => setUser(data.user))
          .catch(() => setUser(false))
          .finally(() => {
            window.history.replaceState(null, "", window.location.pathname);
          });
        return;
      }
    }
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await authLogin({ email, password });
    setUser(data.user);
  };

  const register = async (email, password, name) => {
    const { data } = await authRegister({ email, password, name });
    setUser(data.user);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await authLogout(); } catch { /* ignore network errors on logout */ }
    setUser(false);
  };

  const setCredits = (credits) =>
    setUser((u) => (u ? { ...u, credits } : u));

  const updateUser = (patch) =>
    setUser((u) => (u ? { ...u, ...patch } : u));

  return (
    <AuthCtx.Provider
      value={{ user, login, register, loginWithGoogle, logout, setCredits, updateUser, refresh: loadMe, authOpen, setAuthOpen }}
    >
      {children}
    </AuthCtx.Provider>
  );
};
